import React, {useState, useEffect} from 'react';

import {Button, Input, Modal, Group, Stack, Text, Textarea, Divider} from '@mantine/core';
import {useCurrentUser} from '../../hooks/useCurrentUser';
import {useModule} from '@/hooks/useModule';
import {useAction, useSelectorWithRefresh} from '@/store/kysely_module';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {notifications} from '@mantine/notifications';
import {useAvatarImage} from '@/hooks/useAvatarImage';
import {AvatarUpload} from '@/components/AvatarUpload';
import {useNavigate} from 'react-router-dom';
import {navigateToPathForReason} from '@/modules/navigation/navigation_module';
import {IconExternalLink} from '@tabler/icons-react';
import {invalidateUserAndAvatarQueries} from '@/utils/react_query_cache';
import {SocialLink} from '@/types/social_links';
import {SocialLinksEditor} from '../profile_sidebar/SocialLinksEditor';
import {stringifySocialLinks, buildSocialUrl} from '@/utils/social_links_helpers';

type Props = {
    data?: {
    };
    close: () => void;
}

export const EditProfileModal = (props: Props) => {
    return (
        <Modal
            title={'Edit Profile'}
            opened={Boolean(props.data)}
            onClose={props.close}
            closeOnClickOutside={false}
            withCloseButton={true}
        >
            {props.data && (
                <EditProfileModalForm
                    data={props.data}
                    close={props.close}
                />
            )}
        </Modal>
    );
};

export type EditProfileModalFormProps = {
    data?: {
    };
    close: () => void;
}

export const EditProfileModalForm = (props: EditProfileModalFormProps) => {
    const currentUser = useCurrentUser()!;
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [username, setUsername] = useState(currentUser.name);
    const [uploadedFileId, setUploadedFileId] = useState<string | null>(currentUser.profile_picture_file_id);
    const [newUploadedBlobUrl, setNewUploadedBlobUrl] = useState<string | null>(null);
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const [bio, setBio] = useState('');
    const [socialLinks, setSocialLinks] = useState<SocialLink[]>([]);

    const userModule = useModule('User');
    const filesModule = useModule('MSFiles');
    const addFileToWorkspaceAction = useAction('addFileToWorkspace');
    const updateObjectAttributeAction = useAction('updateObjectAttribute');

    const {data: existingProfilePictureUrl} = useAvatarImage(currentUser.id, currentUser.profile_picture_file_id, currentUser.updated_at);
    const [profileData] = useSelectorWithRefresh('getUserProfileData', {user_id: currentUser.id}, null, {workspace_id: currentUser.id});

    useEffect(() => {
        if (profileData) {
            if (profileData.bio) {
                setBio(profileData.bio);
            }
            if (profileData.socialLinks) {
                setSocialLinks(profileData.socialLinks);
            }
        }
    }, [profileData]);

    const handleChooseImage = async () => {
        setIsUploadingImage(true);
        try {
            const result = await filesModule.client().chooseFileAndUpload(currentUser.id, {
                shouldUseDirectory: false,
                onUploadStart: (fileName) => {
                    notifications.show({
                        id: 'profile-image-upload',
                        loading: true,
                        title: 'Uploading profile image',
                        message: fileName,
                        autoClose: false,
                    });
                },
                onIsDirectory: () => {},
            });

            notifications.hide('profile-image-upload');

            if (result) {
                const fileInfo = {
                    name: result.name,
                    fullPath: result.fullPath,
                    mimeType: result.mimeType,
                    size: result.size,
                    workspace_id: currentUser.id,
                    hash: result.hash,
                    lastModifiedAt: result.lastModifiedAt || Date.now(),
                };

                const workspaceFile = await addFileToWorkspaceAction(fileInfo, {workspace_id: currentUser.id});
                if (workspaceFile) {
                    setUploadedFileId(workspaceFile.id);
                    const src = await filesModule.client().getFileSrc(workspaceFile);
                    setNewUploadedBlobUrl(src);
                    notifications.show({
                        title: 'Image uploaded',
                        message: 'Profile image uploaded successfully',
                        color: 'green',
                    });
                }
            }
        } catch (error) {
            console.error('Error uploading image:', error);
            notifications.show({
                title: 'Error uploading image',
                message: 'Please try again',
                color: 'red',
            });
        } finally {
            setIsUploadingImage(false);
        }
    };

    const updateProfileMutation = useMutation({
        mutationFn: async () => {
            const trimmedUsername = username.trim();
            const updates: {username?: string; profile_picture_file_id?: string} = {};

            if (trimmedUsername && trimmedUsername !== currentUser.name) {
                updates.username = trimmedUsername;
            }

            if (uploadedFileId) {
                updates.profile_picture_file_id = uploadedFileId;
            }

            if (Object.keys(updates).length > 0) {
                await userModule.updateProfile(updates);
            }

            if (bio.trim()) {
                await updateObjectAttributeAction({
                    parent_id: currentUser.id,
                    parent_type: 'User',
                    attribute: 'bio',
                    value: bio.trim(),
                }, {workspace_id: currentUser.id});
            }

            if (socialLinks.length > 0) {
                const linksWithFullUrls = socialLinks.map(link => ({
                    ...link,
                    url: buildSocialUrl(link.url, link.type)
                }));

                await updateObjectAttributeAction({
                    parent_id: currentUser.id,
                    parent_type: 'User',
                    attribute: 'social_links',
                    value: stringifySocialLinks(linksWithFullUrls),
                }, {workspace_id: currentUser.id});
            }
        },
        onSuccess: async () => {
            await userModule.refreshUser();

            invalidateUserAndAvatarQueries(queryClient, currentUser.id);

            notifications.show({
                title: 'Profile updated',
                message: 'Your profile has been updated successfully',
                color: 'green',
            });
            props.close();
        },
        onError: (error) => {
            console.error('Error updating profile:', error);
            notifications.show({
                title: 'Error updating profile',
                message: 'Please try again',
                color: 'red',
            });
        },
    });

    const currentAvatarSrc = newUploadedBlobUrl || existingProfilePictureUrl;

    return (
        <Stack gap="md" p="md">
            <div>
                <Text size="sm" mb="xs" fw={500}>Profile Picture</Text>
                <AvatarUpload
                    userName={currentUser.name}
                    currentImageUrl={currentAvatarSrc}
                    size="md"
                    onImageSelect={handleChooseImage}
                    isUploading={isUploadingImage}
                    disabled={updateProfileMutation.isPending}
                />
            </div>

            <div>
                <Text size="sm" mb="xs" fw={500}>Nickname</Text>
                <Input
                    size='sm'
                    onChange={e => setUsername(e.target.value)}
                    value={username}
                    autoCapitalize='off'
                    autoComplete='off'
                    autoCorrect='off'
                    spellCheck={false}
                />
            </div>

            <Divider />

            <div>
                <Text size="sm" mb="xs" fw={500}>Bio</Text>
                <Textarea
                    size='sm'
                    placeholder="Tell us about yourself..."
                    value={bio}
                    onChange={e => setBio(e.target.value)}
                    rows={3}
                    maxLength={500}
                />
                <Text size="xs" c="dimmed" mt={4}>
                    {bio.length}/500 characters
                </Text>
            </div>

            <Divider />

            <div>
                <Text size="sm" mb="xs" fw={500}>Social Links</Text>
                <SocialLinksEditor
                    links={socialLinks}
                    onChange={setSocialLinks}
                />
            </div>

            <Group justify="flex-end">
                <Button
                    size='xs'
                    variant="default"
                    onClick={props.close}
                    disabled={updateProfileMutation.isPending}
                >
                    Cancel
                </Button>
                <Button
                    size='xs'
                    disabled={!username || updateProfileMutation.isPending}
                    onClick={() => updateProfileMutation.mutate()}
                    loading={updateProfileMutation.isPending}
                >
                    {updateProfileMutation.isPending ? 'Updating...' : 'Save Changes'}
                </Button>
            </Group>
        </Stack>
    );
};
